/**
 * SeoSettingsTab — site-wide SEO defaults & analytics IDs editor for the
 * institute admin's CMS Management page. Drives:
 *   • <SeoHead> defaults on every CMS page
 *   • Organization JSON-LD (knowledge panel)
 *   • Third-party analytics scripts injected by CmsAnalytics
 *   • robots.txt indexability toggle
 */

import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Save, RefreshCw, Search, BarChart3, Building2, Globe2, Shield, Sparkles, Activity } from 'lucide-react';
import {
  AiModelPicker, AppButton, AppCard, AppCardContent, AppCardHeader, AppCardTitle,
  Checkbox, FormField, Input, Radio, Textarea,
} from '../ui';
import Shimmer from '../Common/Shimmer';
import useAiModels from '../../hooks/useAiModels';
import apiClient from '../../utils/apiClient';
import {
  getSeoSettings, updateSeoSettings, aiGenerateSeo, aiAuditSeo,
  uploadCmsImage,
} from '../../utils/cmsAdminApi';
import { usePlan } from '../../contexts/PlanContext';
import SeoAuditModal from './SeoAuditModal';

const EMPTY_SEO = {
  indexable: true,
  defaultMetaTitle: '',
  titleSuffix: '',
  defaultMetaDescription: '',
  defaultKeywords: [],
  defaultOgImage: '',
  faviconMode: 'branding',
  faviconS3Key: '',
  favicon: '',
  canonicalBaseUrl: '',
  social: {
    twitter: '', facebook: '', instagram: '', linkedin: '', youtube: '',
  },
  organization: {
    legalName: '', logoUrl: '', contactEmail: '', contactPhone: '',
    streetAddress: '', addressLocality: '', addressRegion: '', postalCode: '',
    addressCountry: 'IN', foundingYear: '',
  },
  verification: {
    googleSiteVerification: '', bingSiteVerification: '', facebookDomainVerification: '',
  },
  analytics: {
    ga4MeasurementId: '', gtmContainerId: '', facebookPixelId: '',
    microsoftClarityId: '', hotjarSiteId: '', linkedInInsightTagId: '',
  },
};

const merge = (base, incoming) => {
  if (!incoming || typeof incoming !== 'object') return base;
  const out = { ...base };
  for (const k of Object.keys(base)) {
    if (incoming[k] === undefined || incoming[k] === null) continue;
    if (Array.isArray(base[k])) {
      out[k] = Array.isArray(incoming[k]) ? incoming[k] : base[k];
    } else if (typeof base[k] === 'object') {
      out[k] = merge(base[k], incoming[k]);
    } else {
      out[k] = incoming[k];
    }
  }
  return out;
};

const SeoSettingsTab = ({ onShowToast, onApplyPageSeo }) => {
  const [seo, setSeo] = useState(EMPTY_SEO);
  const [original, setOriginal] = useState(EMPTY_SEO);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditReport, setAuditReport] = useState(null);
  const [auditTokens, setAuditTokens] = useState(null);
  const [brandingFaviconUrl, setBrandingFaviconUrl] = useState(null);
  const [cmsFaviconUploading, setCmsFaviconUploading] = useState(false);
  const [customFaviconPreview, setCustomFaviconPreview] = useState('');
  const { hasPlanFeature } = usePlan();
  const aiAvailable = hasPlanFeature?.('aiCmsContent');
  // Both AI buttons on this tab (draft the site-wide meta, audit the whole site)
  // run the same picked model. Only fetched when the plan actually has the
  // feature — otherwise the buttons do not exist and neither should the request.
  const ai = useAiModels('/cms/ai/models', !!aiAvailable);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, favRes] = await Promise.all([
        getSeoSettings(),
        apiClient.get('/institute/settings/favicon-url').catch(() => ({ data: {} })),
      ]);
      const merged = merge(EMPTY_SEO, r?.data || {});
      setSeo(merged);
      setOriginal(JSON.parse(JSON.stringify(merged)));
      setCustomFaviconPreview(r?.data?.faviconPreviewUrl || '');
      setBrandingFaviconUrl(favRes?.data?.url || null);
    } catch {
      setSeo(EMPTY_SEO); setOriginal(EMPTY_SEO); setCustomFaviconPreview('');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const dirty = JSON.stringify(seo) !== JSON.stringify(original);

  const set = (path, value) => {
    setSeo((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      const parts = path.split('.');
      let ref = next;
      for (let i = 0; i < parts.length - 1; i++) ref = ref[parts[i]];
      ref[parts[parts.length - 1]] = value;
      return next;
    });
  };

  const setKeywords = (csv) => {
    const arr = (csv || '').split(',').map((s) => s.trim()).filter(Boolean);
    set('defaultKeywords', arr);
  };

  const handleCmsFaviconFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      onShowToast?.('Please choose a PNG or ICO image', 'error');
      e.target.value = '';
      return;
    }
    setCmsFaviconUploading(true);
    try {
      const res = await uploadCmsImage(file, 'cms-favicon');
      if (!res?.success || !res.key) throw new Error('upload');
      setSeo((prev) => ({
        ...prev,
        faviconMode: 'custom',
        faviconS3Key: res.key,
        favicon: '',
      }));
      setCustomFaviconPreview(res.url || '');
      onShowToast?.('Favicon uploaded — Save to apply on the marketing site');
    } catch {
      onShowToast?.('Favicon upload failed', 'error');
    } finally {
      setCmsFaviconUploading(false);
      e.target.value = '';
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await updateSeoSettings(seo);
      const d = result?.data;
      if (d) {
        const merged = merge(EMPTY_SEO, d);
        setSeo(merged);
        setOriginal(JSON.parse(JSON.stringify(merged)));
        setCustomFaviconPreview(d.faviconPreviewUrl || '');
      } else {
        setOriginal(JSON.parse(JSON.stringify(seo)));
      }
      onShowToast?.('SEO settings saved');
    } catch (e) {
      onShowToast?.(e?.response?.data?.message || 'Failed to save SEO settings', 'error');
    } finally { setSaving(false); }
  };

  const handleReset = () => setSeo(original);

  const handleAiGenerate = async () => {
    if (!aiAvailable || aiBusy) return;
    setAiBusy(true);
    try {
      const r = await aiGenerateSeo({
        pageType: 'site',
        context: [
          seo.defaultMetaTitle && `Existing default title: ${seo.defaultMetaTitle}`,
          seo.defaultMetaDescription && `Existing description: ${seo.defaultMetaDescription}`,
          seo.organization?.legalName && `Legal name: ${seo.organization.legalName}`,
        ].filter(Boolean).join('\n'),
        regenerate: !!(seo.defaultMetaTitle || seo.defaultMetaDescription),
        ...ai.payload,
      });
      const d = r?.data || {};
      setSeo((prev) => {
        const next = { ...prev };
        // Always apply the three core meta fields (they're the point of the button).
        next.defaultMetaTitle = d.metaTitle || prev.defaultMetaTitle;
        next.defaultMetaDescription = d.metaDescription || prev.defaultMetaDescription;
        next.defaultKeywords = Array.isArray(d.keywords) && d.keywords.length
          ? d.keywords : prev.defaultKeywords;
        // titleSuffix — fill only if blank
        if (d.titleSuffix && !prev.titleSuffix) next.titleSuffix = d.titleSuffix;
        // Organization fields — only fill blank slots so we never overwrite admin data
        if (d.organization && typeof d.organization === 'object') {
          const o = d.organization;
          const org = { ...prev.organization };
          if (o.legalName       && !org.legalName)       org.legalName       = o.legalName;
          if (o.contactEmail    && !org.contactEmail)     org.contactEmail    = o.contactEmail;
          if (o.contactPhone    && !org.contactPhone)     org.contactPhone    = o.contactPhone;
          if (o.streetAddress   && !org.streetAddress)    org.streetAddress   = o.streetAddress;
          if (o.addressLocality && !org.addressLocality)  org.addressLocality = o.addressLocality;
          if (o.addressRegion   && !org.addressRegion)    org.addressRegion   = o.addressRegion;
          if (o.postalCode      && !org.postalCode)       org.postalCode      = o.postalCode;
          if (o.addressCountry  && !org.addressCountry)   org.addressCountry  = o.addressCountry;
          if (o.foundingYear    && !org.foundingYear)     org.foundingYear    = o.foundingYear;
          next.organization = org;
        }
        return next;
      });
      onShowToast?.('AI suggestions applied — review and Save');
    } catch (e) {
      const msg = e?.response?.data?.message || 'AI generation failed';
      onShowToast?.(msg, 'error');
    } finally { setAiBusy(false); }
  };

  // ── Whole-site SEO audit ─────────────────────────────────────────────────
  const runAudit = async () => {
    if (auditLoading) return;
    setAuditOpen(true);
    setAuditLoading(true);
    setAuditReport(null);
    setAuditTokens(null);
    try {
      const r = await aiAuditSeo(ai.payload);
      setAuditReport(r?.data || null);
      setAuditTokens(r?.usage?.tokensUsed ?? null);
    } catch (e) {
      onShowToast?.(e?.response?.data?.message || 'SEO audit failed', 'error');
      setAuditOpen(false);
    } finally { setAuditLoading(false); }
  };

  // Apply audit's global suggestions into the form (admin still has to click Save).
  const applyGlobalSuggestions = (gs) => {
    setSeo((prev) => ({
      ...prev,
      defaultMetaTitle: gs.defaultMetaTitle || prev.defaultMetaTitle,
      defaultMetaDescription: gs.defaultMetaDescription || prev.defaultMetaDescription,
      defaultKeywords: (gs.defaultKeywords || []).length ? gs.defaultKeywords : prev.defaultKeywords,
      titleSuffix: gs.titleSuffix || prev.titleSuffix,
    }));
    onShowToast?.('Site-wide suggestions applied — review and Save');
    setAuditOpen(false);
  };

  // Apply a per-page recommendation to that page's search listing. It joins
  // the website draft (saved from the page's save bar) rather than being
  // written straight to the server: a direct write was overwritten by the
  // next save of the Pages tab, which still held the old copy.
  const applyPageRecommendation = (pageType, rec) => {
    const seo = {
      ...(rec.metaTitle ? { metaTitle: rec.metaTitle } : {}),
      ...(rec.metaDescription ? { metaDescription: rec.metaDescription } : {}),
      ...((rec.keywords || []).length ? { metaKeywords: rec.keywords } : {}),
    };
    onApplyPageSeo?.(pageType, seo);
    onShowToast?.(`Suggestion applied to the ${pageType} page — save to publish it.`);
  };

  if (loading) return <Shimmer height={120} count={4} />;

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <div className="tw-text-sm tw-leading-normal tw-text-content-muted [&>p]:tw-m-0">
        <p>
          These settings drive page titles, meta descriptions, Open Graph cards, and JSON-LD
          structured data emitted by every page on your public website. They also control
          which analytics scripts are loaded.
        </p>
        {aiAvailable && (
          <div className="tw-mt-2.5 tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-rounded-sm tw-border tw-border-dashed tw-border-primary tw-bg-surface-sunken tw-px-3 tw-py-2.5 [&>small]:tw-text-xs [&>small]:tw-text-content-muted">
            <AppButton variant="secondary" onClick={handleAiGenerate} disabled={aiBusy}>
              <Sparkles size={14} /> {aiBusy ? 'Generating…' : 'Generate site-wide SEO with AI'}
            </AppButton>
            <AppButton variant="primary" onClick={runAudit} disabled={auditLoading}>
              <Activity size={14} /> {auditLoading ? 'Auditing…' : 'Run AI SEO audit'}
            </AppButton>
            <small>AI drafts copy or analyses your whole site. Both deduct tokens from your monthly CMS quota.</small>
            <AiModelPicker {...ai} className="tw-max-w-[420px] tw-basis-full" />
          </div>
        )}
      </div>

      {/* SITE-WIDE SEO DEFAULTS */}
      <AppCard>
        <AppCardHeader>
          <AppCardTitle><Search size={16} /> Site-wide SEO defaults</AppCardTitle>
        </AppCardHeader>
        <AppCardContent>
          <div className="tw-grid tw-grid-cols-1 tw-gap-x-5 tw-gap-y-4 min-[721px]:tw-grid-cols-2">
            <Checkbox
              className="tw-col-span-full tw-rounded-sm tw-border tw-border-solid tw-border-line tw-bg-surface-sunken tw-p-3"
              labelClassName="tw-font-semibold"
              label="Allow search engines to index this site"
              description="Turn off during pre-launch. When off, robots.txt blocks all crawlers."
              checked={!!seo.indexable}
              onChange={(e) => set('indexable', e.target.checked)}
            />

            <FormField
              label="Default page title"
              hint={<>{seo.defaultMetaTitle.length}/70 — used when a page has no title.</>}
            >
              <Input
                type="text"
                value={seo.defaultMetaTitle}
                onChange={(e) => set('defaultMetaTitle', e.target.value)}
                placeholder="e.g. Best Coding Institute in Hyderabad"
                maxLength={70}
              />
            </FormField>

            <FormField
              label="Title suffix"
              hint={<>Appended to every page title (incl. leading separator like “ | ”).</>}
            >
              <Input
                type="text"
                value={seo.titleSuffix}
                onChange={(e) => set('titleSuffix', e.target.value)}
                placeholder=" | Your Institute Name"
              />
            </FormField>

            <FormField
              label="Default meta description"
              hint={<>{seo.defaultMetaDescription.length}/160 ideal. Used when a page has no description.</>}
              className="tw-col-span-full"
            >
              <Textarea
                rows={3}
                value={seo.defaultMetaDescription}
                onChange={(e) => set('defaultMetaDescription', e.target.value)}
                placeholder="One- to two-sentence summary of your institute (160 chars)"
                maxLength={200}
              />
            </FormField>

            <FormField
              label="Default keywords (comma-separated)"
              className="tw-col-span-full"
            >
              <Input
                type="text"
                value={(seo.defaultKeywords || []).join(', ')}
                onChange={(e) => setKeywords(e.target.value)}
                placeholder="java training, mern stack course, software training institute"
              />
            </FormField>

            <div className="tw-col-span-full tw-flex tw-flex-col tw-gap-1.5 tw-text-sm tw-text-content [&>span]:tw-font-medium [&>span]:tw-text-content-muted">
              <span>Marketing site favicon</span>
              <p className="tw-mb-2 tw-text-[0.8125rem] tw-text-content-subtle">
                Tab icon for the <strong>public CMS website</strong> only. LMS tabs still use{' '}
                <Link to="/settings">Institute → Branding</Link> favicon unless you choose custom below.
              </p>
              <div className="tw-mb-2 tw-flex tw-flex-col tw-gap-2">
                <Radio
                  name="cmsFaviconMode"
                  label="Use LMS branding favicon"
                  checked={seo.faviconMode === 'branding'}
                  onChange={() => {
                    set('faviconMode', 'branding');
                    set('faviconS3Key', '');
                    set('favicon', '');
                    setCustomFaviconPreview('');
                  }}
                />
                {seo.faviconMode === 'branding' && brandingFaviconUrl ? (
                  <div className="[&_img]:tw-rounded-[4px] [&_img]:tw-border [&_img]:tw-border-solid [&_img]:tw-border-line">
                    <img src={brandingFaviconUrl} alt="" width={32} height={32} />
                  </div>
                ) : null}
                {seo.faviconMode === 'branding' && !brandingFaviconUrl ? (
                  <small className="tw-block tw-text-content-muted">No favicon in Institute branding — the browser default icon is used.</small>
                ) : null}
                <Radio
                  name="cmsFaviconMode"
                  label="Custom favicon for marketing site"
                  checked={seo.faviconMode === 'custom'}
                  onChange={() => set('faviconMode', 'custom')}
                />
              </div>
              {seo.faviconMode === 'custom' ? (
                <div>
                  <input
                    type="file"
                    accept="image/png,image/x-icon,image/vnd.microsoft.icon,.ico"
                    hidden
                    id="seo-cms-favicon-file"
                    onChange={handleCmsFaviconFile}
                    disabled={cmsFaviconUploading || saving}
                  />
                  <div className="tw-mb-2 tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                    <AppButton as="label" htmlFor="seo-cms-favicon-file" variant="outline" size="sm" className="tw-mb-0 tw-cursor-pointer">
                      {cmsFaviconUploading ? 'Uploading…' : 'Upload favicon'}
                    </AppButton>
                    {(customFaviconPreview || seo.favicon?.trim()) ? (
                      <img
                        src={customFaviconPreview || seo.favicon}
                        alt=""
                        width={32}
                        height={32}
                        className="tw-rounded-[4px] tw-border tw-border-solid tw-border-line tw-object-contain"
                      />
                    ) : null}
                    <button
                      type="button"
                      className="tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0 tw-text-[0.8125rem] tw-text-primary tw-underline disabled:tw-cursor-not-allowed disabled:tw-opacity-50"
                      disabled={!seo.faviconS3Key && !seo.favicon?.trim()}
                      onClick={() => {
                        set('faviconS3Key', '');
                        set('favicon', '');
                        setCustomFaviconPreview('');
                      }}
                    >
                      Clear custom favicon
                    </button>
                  </div>
                  <FormField
                    label="Or paste favicon URL"
                  >
                    <Input
                      type="url"
                      value={seo.favicon}
                      onChange={(e) => {
                      set('favicon', e.target.value);
                      if (e.target.value.trim()) {
                      set('faviconS3Key', '');
                      setCustomFaviconPreview('');
                      }
                      }}
                      placeholder="https://cdn.example.com/favicon.ico"
                    />
                  </FormField>
                </div>
              ) : null}
            </div>

            <FormField
              label="Default OG image URL"
            >
              <Input
                type="text"
                value={seo.defaultOgImage}
                onChange={(e) => set('defaultOgImage', e.target.value)}
                placeholder="https://cdn.example.com/og.png (1200×630)"
              />
            </FormField>

            <FormField
              label="Canonical base URL"
              hint={<>Used in &lt;link rel="canonical"&gt; and sitemap.xml. Leave blank to use current host.</>}
              className="tw-col-span-full"
            >
              <Input
                type="text"
                value={seo.canonicalBaseUrl}
                onChange={(e) => set('canonicalBaseUrl', e.target.value)}
                placeholder="https://www.your-institute.com"
              />
            </FormField>
          </div>
        </AppCardContent>
      </AppCard>

      {/* ORGANIZATION JSON-LD */}
      <AppCard>
        <AppCardHeader>
          <AppCardTitle><Building2 size={16} /> Organization details (Knowledge Panel)</AppCardTitle>
        </AppCardHeader>
        <AppCardContent>
          <p className="tw-mb-3.5 tw-text-[0.8125rem] tw-text-content-subtle">
            Used by Google to build the knowledge panel and rich results for your institute.
          </p>
          <div className="tw-grid tw-grid-cols-1 tw-gap-x-5 tw-gap-y-4 min-[721px]:tw-grid-cols-2">
            <FormField
              label="Legal name"
            >
              <Input type="text" value={seo.organization.legalName}
                              onChange={(e) => set('organization.legalName', e.target.value)}
                              placeholder="Acme Training Pvt Ltd" />
            </FormField>
            <FormField
              label="Logo URL (square)"
            >
              <Input type="text" value={seo.organization.logoUrl}
                              onChange={(e) => set('organization.logoUrl', e.target.value)}
                              placeholder="https://cdn.example.com/logo.png" />
            </FormField>
            <FormField
              label="Contact email"
            >
              <Input type="email" value={seo.organization.contactEmail}
                              onChange={(e) => set('organization.contactEmail', e.target.value)}
                              placeholder="info@institute.com" />
            </FormField>
            <FormField
              label="Contact phone"
            >
              <Input type="text" value={seo.organization.contactPhone}
                              onChange={(e) => set('organization.contactPhone', e.target.value)}
                              placeholder="+91 98765 43210" />
            </FormField>
            <FormField
              label="Street address"
              className="tw-col-span-full"
            >
              <Input type="text" value={seo.organization.streetAddress}
                              onChange={(e) => set('organization.streetAddress', e.target.value)}
                              placeholder="123 Main Road, Tech Park" />
            </FormField>
            <FormField
              label="City / locality"
            >
              <Input type="text" value={seo.organization.addressLocality}
                              onChange={(e) => set('organization.addressLocality', e.target.value)}
                              placeholder="Hyderabad" />
            </FormField>
            <FormField
              label="State / region"
            >
              <Input type="text" value={seo.organization.addressRegion}
                              onChange={(e) => set('organization.addressRegion', e.target.value)}
                              placeholder="Telangana" />
            </FormField>
            <FormField
              label="Postal code"
            >
              <Input type="text" value={seo.organization.postalCode}
                              onChange={(e) => set('organization.postalCode', e.target.value)}
                              placeholder="500032" />
            </FormField>
            <FormField
              label="Country code"
            >
              <Input type="text" value={seo.organization.addressCountry}
                              onChange={(e) => set('organization.addressCountry', e.target.value)}
                              placeholder="IN" maxLength={3} />
            </FormField>
            <FormField
              label="Founding year"
            >
              <Input type="text" value={seo.organization.foundingYear}
                              onChange={(e) => set('organization.foundingYear', e.target.value)}
                              placeholder="2018" />
            </FormField>
          </div>
        </AppCardContent>
      </AppCard>

      {/* SOCIAL */}
      <AppCard>
        <AppCardHeader>
          <AppCardTitle><Globe2 size={16} /> Social profiles (sameAs)</AppCardTitle>
        </AppCardHeader>
        <AppCardContent>
          <div className="tw-grid tw-grid-cols-1 tw-gap-x-5 tw-gap-y-4 min-[721px]:tw-grid-cols-2">
            <FormField
              label="Twitter / X handle"
            >
              <Input type="text" value={seo.social.twitter}
                              onChange={(e) => set('social.twitter', e.target.value.replace(/^@/, ''))}
                              placeholder="myinstitute (without @)" />
            </FormField>
            <FormField
              label="Facebook URL"
            >
              <Input type="text" value={seo.social.facebook}
                              onChange={(e) => set('social.facebook', e.target.value)}
                              placeholder="https://facebook.com/myinstitute" />
            </FormField>
            <FormField
              label="Instagram URL"
            >
              <Input type="text" value={seo.social.instagram}
                              onChange={(e) => set('social.instagram', e.target.value)}
                              placeholder="https://instagram.com/myinstitute" />
            </FormField>
            <FormField
              label="LinkedIn URL"
            >
              <Input type="text" value={seo.social.linkedin}
                              onChange={(e) => set('social.linkedin', e.target.value)}
                              placeholder="https://linkedin.com/company/myinstitute" />
            </FormField>
            <FormField
              label="YouTube URL"
            >
              <Input type="text" value={seo.social.youtube}
                              onChange={(e) => set('social.youtube', e.target.value)}
                              placeholder="https://youtube.com/@myinstitute" />
            </FormField>
          </div>
        </AppCardContent>
      </AppCard>

      {/* ANALYTICS */}
      <AppCard>
        <AppCardHeader>
          <AppCardTitle><BarChart3 size={16} /> Analytics & tracking</AppCardTitle>
        </AppCardHeader>
        <AppCardContent>
          <p className="tw-mb-3.5 tw-text-[0.8125rem] tw-text-content-subtle">
            Add only the IDs you want to use — empty fields are ignored. All scripts load
            asynchronously after the main content.
          </p>
          <div className="tw-grid tw-grid-cols-1 tw-gap-x-5 tw-gap-y-4 min-[721px]:tw-grid-cols-2">
            <FormField
              label="Google Analytics 4"
            >
              <Input type="text" value={seo.analytics.ga4MeasurementId}
                              onChange={(e) => set('analytics.ga4MeasurementId', e.target.value)}
                              placeholder="G-XXXXXXXXXX" />
            </FormField>
            <FormField
              label="Google Tag Manager"
            >
              <Input type="text" value={seo.analytics.gtmContainerId}
                              onChange={(e) => set('analytics.gtmContainerId', e.target.value)}
                              placeholder="GTM-XXXXXXX" />
            </FormField>
            <FormField
              label="Meta (Facebook) Pixel"
            >
              <Input type="text" value={seo.analytics.facebookPixelId}
                              onChange={(e) => set('analytics.facebookPixelId', e.target.value)}
                              placeholder="1234567890123456" />
            </FormField>
            <FormField
              label="Microsoft Clarity"
            >
              <Input type="text" value={seo.analytics.microsoftClarityId}
                              onChange={(e) => set('analytics.microsoftClarityId', e.target.value)}
                              placeholder="abcd1234ef" />
            </FormField>
            <FormField
              label="Hotjar Site ID"
            >
              <Input type="text" value={seo.analytics.hotjarSiteId}
                              onChange={(e) => set('analytics.hotjarSiteId', e.target.value)}
                              placeholder="1234567" />
            </FormField>
            <FormField
              label="LinkedIn Insight Tag"
            >
              <Input type="text" value={seo.analytics.linkedInInsightTagId}
                              onChange={(e) => set('analytics.linkedInInsightTagId', e.target.value)}
                              placeholder="1234567" />
            </FormField>
          </div>
        </AppCardContent>
      </AppCard>

      {/* VERIFICATION */}
      <AppCard>
        <AppCardHeader>
          <AppCardTitle><Shield size={16} /> Search Console verification</AppCardTitle>
        </AppCardHeader>
        <AppCardContent>
          <div className="tw-grid tw-grid-cols-1 tw-gap-x-5 tw-gap-y-4 min-[721px]:tw-grid-cols-2">
            <FormField
              label="Google site verification"
            >
              <Input type="text" value={seo.verification.googleSiteVerification}
                              onChange={(e) => set('verification.googleSiteVerification', e.target.value)}
                              placeholder="content value from Google Search Console meta tag" />
            </FormField>
            <FormField
              label="Bing site verification"
            >
              <Input type="text" value={seo.verification.bingSiteVerification}
                              onChange={(e) => set('verification.bingSiteVerification', e.target.value)}
                              placeholder="content value from Bing Webmaster meta tag" />
            </FormField>
            <FormField
              label="Facebook domain verification"
            >
              <Input type="text" value={seo.verification.facebookDomainVerification}
                              onChange={(e) => set('verification.facebookDomainVerification', e.target.value)} />
            </FormField>
          </div>
        </AppCardContent>
      </AppCard>

      <div className="tw-flex tw-justify-end tw-gap-2 tw-pb-1 tw-pt-2">
        <AppButton variant="ghost" onClick={handleReset} disabled={!dirty || saving}>
          <RefreshCw size={14} /> Reset
        </AppButton>
        <AppButton onClick={handleSave} disabled={!dirty || saving}>
          <Save size={14} /> {saving ? 'Saving…' : 'Save SEO settings'}
        </AppButton>
      </div>

      <SeoAuditModal
        show={auditOpen}
        onHide={() => setAuditOpen(false)}
        loading={auditLoading}
        report={auditReport}
        tokensUsed={auditTokens}
        onApplyGlobal={applyGlobalSuggestions}
        onApplyPage={applyPageRecommendation}
        onRerun={runAudit}
      />
    </div>
  );
};

export default SeoSettingsTab;
